import querystring from 'querystring';

import Auth0Identity from './identity';
import {
  Auth0ClientOptions,
  Auth0RedirectCallbackResponse,
  Auth0LoginErrorResponse
} from './auth0Types';
import { logPrefix } from './constants';
import { transformAuth0Claims } from '../utils/transform';

//remove after ref ui & sales API implemented
const id = 'VuTdg8C48TQ15ni148QzadSG9eEplYxi';
const domain = 'dev-dersptm1.auth0.com';
const redirect = window.location.origin;

/**
 * Authenticates user via Auth0 by redirecting to Auth0 login page then returning to desired redirect url
 *
 * @param options optional Auth0 login options.
 *
 * Note, this login method does not complete the authentication process. This login method will redirect to the specified redirect_uri after authenticating the user.
 * The Auth0 `loginWithRedirect()`, appends `code` and `state` query params to the `redirect_uri`.
 * On redirect, call `Auth0Identity.handleLoginRedirect` to complete the user Authentication. This method uses the previously mentioned `code` and `state` to complete the authentication process.
 *
 * <details>
 * ```
 * See documentation for more info:
 * https://auth0.github.io/auth0-spa-js/classes/auth0client.html#loginwithredirect
 * ```
 * </details>
 */

export default async function login(options?: Auth0ClientOptions): Promise<void> {
  return await Auth0Identity.auth0.loginWithRedirect({ ...options });
}

/**
 * Determines if a user is logged in or not using the Auth0 method `isAuthenticated()`
 *
 * @returns _Promise_ which resolves to either true or false.
 *
 * ```
 * See documentation for more info:
 * https://auth0.github.io/auth0-spa-js/classes/auth0client.html#isauthenticated
 * ```
 */
export async function isLoggedIn(): Promise<boolean> {
  try {
    const isLoggedIn = await Auth0Identity.auth0.isAuthenticated();
    if (isLoggedIn && (!Auth0Identity.userIdentity || !Auth0Identity.userIdentity.accessToken)) {
      const tokenInfo = await Auth0Identity.auth0.getIdTokenClaims();
      const transformedAuth0Claims = transformAuth0Claims(tokenInfo);
      Auth0Identity.userIdentity = transformedAuth0Claims;
    }
    return isLoggedIn;
  } catch (err) {
    throw err;
  }
}

/**
 * Determines whether or not Auth0 is initialized
 *
 * @returns true or false
 *
 */
export async function isAuth0Initialized() {
  return !!(Auth0Identity.auth0 && Auth0Identity.auth0.cache);
}

/**
 * Completes user authentication after call to `Auth0Identity.login()` redirects back to specified url.
 *
 * @param options optional Auth0 login options.
 *
 * This method must be called to complete user authentication after the `Auth0Identity.login` method is resolved. Depending on the result of the authentication attempt, `loginWithRedirect()` will append different query parameters to the `redirect_uri`.
 * If the authentication is successful, you will find `code` and `state` parameters. If the authentication is unsuccessful, you will find `error` and `error_description`.
 * On successful redirect, call this method and it will either complete the user login, or throw an error if the authentication was not successful.
 *
 * <details>
 * ```
 * See documentation for more info:
 * https://auth0.github.io/auth0-spa-js/classes/auth0client.html#handleredirectcallback
 * ```
 * </details>
 */
export async function handleLoginRedirect(): Promise<
  Auth0RedirectCallbackResponse | Auth0LoginErrorResponse
> {
  const queryString = querystring.parse(window.location.search.slice(1));
  const loginError = queryString['error'] || queryString['error_description'];
  const loginInfo = queryString['code'] && queryString['state'];
  if (loginError) {
    throw queryString;
  } else if (loginInfo) {
    try {
      const user = await Auth0Identity.auth0.handleRedirectCallback();
      const tokenInfo = await Auth0Identity.auth0.getIdTokenClaims();
      const transformedAuth0Claims = transformAuth0Claims(tokenInfo);
      Auth0Identity.userIdentity = transformedAuth0Claims;
      window.history.replaceState({}, document.title, '/');
      return user;
    } catch (err) {
      throw err;
    }
  } else {
    throw `${logPrefix} - The login attempt was unsuccessful.`;
  }
}

//remove after ref ui implemented
window.onload = async () => {
  const queryString = querystring.parse(window.location.search.slice(1));
  const loginResponse = queryString['code'] || queryString['error'];
  const auth0Initialized = await isAuth0Initialized();
  if (!auth0Initialized) {
    await Auth0Identity.initAuth0({
      client_id: id,
      domain,
      redirect_uri: redirect,
      prompt: 'consent',
      display: 'page'
    });
  }
  if (loginResponse) {
    handleLoginRedirect();
  }
};
